import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { IoSearch } from "react-icons/io5";
import { FaMagnifyingGlassPlus } from "react-icons/fa6";
import Keyboard from "simple-keyboard";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import Proximiio from "proximiio-js-library";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useMapStore from "@/store/mapStore";
import useParkAssist, {
  FetchStatus,
  ParkAssistImage,
} from "@/hooks/useParkAssist";
import { Skeleton } from "./ui/skeleton";
import useFeaturesStore from "@/store/featuresStore";
import { cn, findFeatureByBayId } from "@/lib/utils";
import {
  arabicLayout,
  chineseLayout,
  englishLayout,
  keyboardsLayout,
  russianLayout,
  zhLayoutCandidates,
} from "@/lib/keyboardLayouts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import useNavigateToAmenity from "@/hooks/useNavigateToAmenity";

function FindMyCar() {
  const kioskMode = useMapStore((state) => state.kioskMode);
  const features = useFeaturesStore((state) => state.features);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const currentLang = useMapStore((state) => state.currentLang);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const appSession = useMapStore((state) => state.appSession);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const setOverlayContent = useMapStore((state) => state.setOverlayContent);
  const centerToFinish = useMapStore((state) => state.centerToFinish);

  const keyboard = useRef<Keyboard>();
  const [search, setSearch] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [message, setMessage] = useState("");
  const [imageData, fetchStatus] = useParkAssist({ plateText: licensePlate });
  const [navigateToFacility] = useNavigateToAmenity();

  const saveLog = useCallback(() => {
    //save parkassist request log
    console.log("LOG: save parkassist request log");
    const userData = {
      osName,
      browserName,
      mobileModel,
      mobileVendor,
      deviceType:
        kioskMode && !mobileMode && activeKiosk?.name
          ? activeKiosk?.name
          : deviceType,
    };
    new Proximiio.InteractionLogger({
      interactionType: "parkassist",
      searchValue: search,
      searchSuccess: imageData.length > 0,
      kioskId: activeKiosk?.id ? activeKiosk?.id : undefined,
      kioskTitle: activeKiosk?.name ? activeKiosk?.name : undefined,
      metadata: userData,
      language: currentLang,
      session: appSession,
    });
  }, [
    activeKiosk?.id,
    activeKiosk?.name,
    appSession,
    currentLang,
    search,
    kioskMode,
    mobileMode,
    imageData,
  ]);

  useEffect(() => {
    if (fetchStatus === FetchStatus.Loaded && search === licensePlate) {
      saveLog();
    }
  }, [fetchStatus, imageData, saveLog, search]);

  useEffect(() => {
    return () => {
      if (keyboard.current?.activeButtonClass) {
        keyboard.current?.destroy();
        keyboard.current = {} as Keyboard;
      }
    };
  }, []);

  const onSelectHandle = useCallback(() => {
    console.log("onSelectHandle", search);
    if (!search.length || search.length < 3) {
      return;
    }
    setMessage("");
    console.log("onSelectHandle", search);
    setLicensePlate(search);
  }, [search]);

  const onChange = (input: string, e: MouseEvent | undefined) => {
    e?.stopPropagation();
    setSearch(input);
    console.log("onChange", input);
  };

  const onKeyPress = (button: string, e: MouseEvent | undefined) => {
    console.log("onKeyPress", button);
    e?.stopPropagation();
    if (button === "{enter}") {
      onSelectHandle();
    }
    if (button === "{shift}" || button === "{lock}") {
      handleShift();
    }
    if (button === "{change-keyboard}") {
      handleKeyboardChange();
    }
    if (button === "{keyboard-en}") {
      handleLangChange("en");
    }
    if (button === "{keyboard-ar}") {
      handleLangChange("ar");
    }
    if (button === "{keyboard-zh}") {
      handleLangChange("zh");
    }
    if (button === "{keyboard-ru}") {
      handleLangChange("ru");
    }
    if (button === "{space}") {
      keyboard.current?.setOptions({
        layoutName: "shift",
      });
    }
    if (
      button !== "{space}" &&
      button !== "{bksp}" &&
      button !== "{change-keyboard}" &&
      button !== "{keyboard-en}" &&
      button !== "{keyboard-ar}" &&
      button !== "{keyboard-zh}" &&
      button !== "{keyboard-ru}"
    ) {
      keyboard.current?.setOptions({
        layoutName: "default",
      });
    }
    if (button === "{bksp}") {
      const lastLetter = keyboard.current?.getInput().slice(-1);
      const isUppercase =
        keyboard.current?.getInput().length === 1 ||
        (lastLetter && /^[A-Z]/.test(lastLetter));
      keyboard.current?.setOptions({
        layoutName: isUppercase ? "shift" : "default",
      });
    }
  };

  const handleShift = () => {
    const currentLayout = keyboard.current?.options.layoutName;
    const shiftToggle = currentLayout === "default" ? "shift" : "default";

    keyboard.current?.setOptions({
      layoutName: shiftToggle,
    });
  };

  const handleKeyboardChange = () => {
    keyboard.current?.setOptions({
      layout: keyboardsLayout,
    });
  };

  const handleLangChange = (language: string) => {
    let layout = englishLayout;
    if (language === "ar") layout = arabicLayout;
    if (language === "en") layout = englishLayout;
    if (language === "ru") layout = russianLayout;
    if (language === "zh") layout = chineseLayout;

    keyboard.current?.setOptions({
      layout,
    });
  };

  const getLanguageLayout = () => {
    if (currentLang === "ar") return arabicLayout;
    if (currentLang === "en") return englishLayout;
    if (currentLang === "ru") return russianLayout;
    if (currentLang === "zh") return chineseLayout;
    return englishLayout;
  };

  const focusHandler = () => {
    if (
      kioskMode &&
      import.meta.env.VITE_WAYFINDING_USE_VIRTUAL_KEYBOARD_AT_KIOSKS ===
        "true" &&
      !keyboard.current?.activeButtonClass &&
      !mobileMode
    ) {
      setTimeout(() => {
        keyboard.current = new Keyboard({
          preventMouseDownDefault: true,
          onChange: (input, e) => onChange(input, e),
          onKeyPress: (button, e) => onKeyPress(button, e),
          layout: getLanguageLayout(),
          layoutCandidates:
            currentLang === "zh" ? zhLayoutCandidates : undefined,
          // rtl: currentLang === "ar",
          mergeDisplay: true,
          display: {
            "{enter}": currentLang === "ar" ? "\u2BA1" : "\u2BA0",
            "{bksp}": currentLang === "ar" ? "\uD83E\uDC26" : "\uD83E\uDC24",
            "{change-keyboard}": t("change-language"),
            "{keyboard-en}": "English keyboard",
            "{keyboard-ar}": `Arabic keyboard <br/> <small>لوحة المفاتيح العربية</small>`,
            /*"{keyboard-zh}": `Chinese keyboard <br/> <small>中文键盘</small>`,
            "{keyboard-ru}": `Russian keyboard <br/> <small>русская клавиатура</small>`,*/
          },
          maxLength: 20,
          layoutName: "shift",
        });
      }, 100);
    }
  };

  const blurHandler = () => {
    if (
      kioskMode &&
      import.meta.env.VITE_WAYFINDING_USE_VIRTUAL_KEYBOARD_AT_KIOSKS ===
        "true" &&
      keyboard.current?.activeButtonClass
    ) {
      keyboard.current?.destroy();
      keyboard.current = {} as Keyboard;
    }
  };

  const carSelectHandler = (bayId: number) => {
    const bayFeature = findFeatureByBayId(bayId, features);
    console.log("should navigate to bay", bayFeature);
    if (bayFeature) {
      setRouteFinish(bayFeature);
      centerToFinish();
      setOverlayContent(undefined);
    } else {
      setMessage(t("unable-to-locate-car"));
    }
  };

  const customerServiceClick = () => {
    navigateToFacility({ amenityTitle: "customer-service-desk" });
  };

  return (
    <OverlayScrollbarsComponent
      className={cn(
        "h-full w-full overflow-y-auto overflow-x-visible group-data-[dir=ltr]:pr-5 group-data-[dir=rtl]:pl-5",
      )}
      options={{
        scrollbars: { autoHide: "move", autoHideDelay: 3000 },
        overflow: {
          x: "visible",
        },
      }}
      defer
    >
      <div
        className={cn(
          "mt-10 flex flex-col items-center kiosk:mt-0 kiosk:h-full kiosk:justify-center",
          mobileMode && "mt-0 items-center justify-start",
          mobileMode && !licensePlate && "mt-0 justify-start",
        )}
      >
        {(!mobileMode ||
          (!licensePlate && fetchStatus === FetchStatus.Loading)) && (
          <>
            <h1
              className={cn(
                "mb-5 text-3xl font-bold",
                mobileMode && "mb-1 text-xl",
              )}
            >
              {t("find-my-car")}
            </h1>
            <p className={cn("text-center", mobileMode ? "mb-5" : "mb-10")}>
              {t("type-as-much")}
            </p>
            <div
              className={cn(
                "mb-1 flex w-full max-w-lg items-center",
                mobileMode && "flex-col gap-5",
              )}
            >
              <Input
                type="text"
                placeholder="A12345"
                className="rounded-full border-primary"
                minLength={4}
                max={20}
                autoFocus
                onFocus={focusHandler}
                onBlur={blurHandler}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Button
                type="submit"
                className="rounded-full px-6 group-data-[dir=ltr]:ml-2 group-data-[dir=rtl]:mr-2"
                onClick={onSelectHandle}
                disabled={!search.length || search.length < 3}
              >
                <IoSearch className="text-lg group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />{" "}
                {t("searchPlaceholder")}
              </Button>
            </div>
            {search && search.length < 3 && (
              <p
                className={cn(
                  "w-full max-w-lg text-xs text-primary group-data-[dir=ltr]:ml-8 group-data-[dir=rtl]:mr-8",
                  mobileMode &&
                    "mt-2 text-center group-data-[dir=ltr]:ml-0 group-data-[dir=rtl]:mr-0",
                )}
              >
                {t("input-minimum-3-characters")}
              </p>
            )}
          </>
        )}
        {licensePlate && fetchStatus === FetchStatus.Loading && (
          <div
            className={cn(
              "mt-10 flex gap-8",
              mobileMode && "mt-0 flex-wrap justify-center gap-4",
            )}
          >
            <Skeleton
              className={cn(
                "flex h-60 w-96 items-center justify-center rounded-2xl",
                mobileMode && "h-24 w-40",
              )}
            >
              <div className="h-5 w-5 animate-spin rounded-full border border-gray-300 border-t-primary" />
            </Skeleton>
            <Skeleton
              className={cn(
                "flex h-60 w-96 items-center justify-center rounded-2xl",
                mobileMode && "h-24 w-40",
              )}
            >
              <div className="h-5 w-5 animate-spin rounded-full border border-gray-300 border-t-primary" />
            </Skeleton>
            <Skeleton
              className={cn(
                "flex h-60 w-96 items-center justify-center rounded-2xl",
                mobileMode && "h-24 w-40",
              )}
            >
              <div className="h-5 w-5 animate-spin rounded-full border border-gray-300 border-t-primary" />
            </Skeleton>
          </div>
        )}
        {!message &&
          fetchStatus === FetchStatus.Loaded &&
          imageData.length > 0 && (
            <div className={cn("mt-20", mobileMode && "mt-0")}>
              <h2
                className={cn(
                  "mb-10 text-center text-xl font-bold",
                  mobileMode && "mb-5 text-base",
                )}
              >
                {t("select-car-image")}
              </h2>
              <ul
                className={cn(
                  "flex justify-center gap-8 px-4",
                  mobileMode && "flex-wrap justify-center gap-4",
                )}
              >
                {(imageData as ParkAssistImage[]).map((item) => (
                  <li key={item.visitId} className="relative">
                    <Dialog>
                      <DialogTrigger className="absolute top-5 z-10 cursor-pointer rounded-full bg-primary p-3 text-white hover:bg-accent group-data-[dir=ltr]:-right-3 group-data-[dir=rtl]:-left-3">
                        <FaMagnifyingGlassPlus />
                      </DialogTrigger>
                      <DialogContent
                        className="max-w-96 translate-y-0 rounded-md border-0 p-0 sm:max-w-lg sm:translate-y-[-50%] sm:rounded-xl md:max-w-2xl lg:max-w-3xl"
                        closeClassname="-top-10 group-data-[dir=ltr]:right-2 group-data-[dir=rtl]:left-2 bg-primary text-white text-xs *:w-5 *:h-5 p-1 focus:ring-0 focus:ring-offset-0 hover:text-white"
                      >
                        <DialogHeader>
                          <DialogDescription>
                            <img
                              src={item.imageUrl}
                              alt=""
                              className={cn("w-full rounded-md sm:rounded-xl")}
                              onClick={() => carSelectHandler(item.bayId)}
                            />
                          </DialogDescription>
                        </DialogHeader>
                      </DialogContent>
                    </Dialog>
                    <img
                      src={item.imageUrl}
                      alt=""
                      className={cn(
                        "h-60 w-96 cursor-pointer rounded-md object-cover sm:rounded-2xl",
                        mobileMode && "h-24 w-40",
                      )}
                      onClick={() => carSelectHandler(item.bayId)}
                    />
                  </li>
                ))}
              </ul>
              <h2
                className={cn(
                  "mx-auto mt-10 max-w-2xl text-center text-xl",
                  mobileMode && "mt-5 text-sm font-normal",
                )}
              >
                {t("no-matching-cars")}
                {"? "}
                {t("could-be-parked-outside")} {t("please")}{" "}
                <span
                  className="cursor-pointer text-primary"
                  onClick={customerServiceClick}
                >
                  {t("contact-support")}
                </span>{" "}
                {t("for-assistance")}
              </h2>
            </div>
          )}
        {!message &&
          fetchStatus === FetchStatus.Loaded &&
          imageData.length === 0 && (
            <div className={cn("mt-10", mobileMode && "mt-5")}>
              <h2
                className={cn(
                  "mb-10 max-w-2xl text-center text-xl",
                  mobileMode && "mb-5 text-base",
                )}
              >
                {t("no-matching-cars")}
                {". "}
                {t("could-be-parked-outside")} {t("please")}{" "}
                <span
                  className="cursor-pointer text-primary"
                  onClick={customerServiceClick}
                >
                  {t("contact-support")}
                </span>{" "}
                {t("for-assistance")}
              </h2>
            </div>
          )}
        {!message &&
          fetchStatus === FetchStatus.Error &&
          imageData.length === 0 && (
            <div className={cn("mt-10", mobileMode && "mt-5")}>
              <h2
                className={cn(
                  "mb-10 max-w-2xl text-center text-xl",
                  mobileMode && "mb-5 text-base",
                )}
              >
                {t("cannot-connect")} {t("please")}{" "}
                <span
                  className="cursor-pointer text-primary"
                  onClick={customerServiceClick}
                >
                  {t("contact-support")}
                </span>{" "}
                {t("for-assistance")}
              </h2>
            </div>
          )}
        {!message && fetchStatus === FetchStatus.TooManyRequests && (
          <div className={cn("mt-10", mobileMode && "mt-5")}>
            <h2
              className={cn(
                "mb-10 max-w-2xl text-center text-xl",
                mobileMode && "mb-5 text-base",
              )}
            >
              {t("too-many-requests")}
            </h2>
          </div>
        )}
        {message && (
          <p
            className={cn(
              "mt-10 text-center text-xl font-bold text-primary",
              mobileMode && "mt-5 text-base",
            )}
          >
            {message}
          </p>
        )}
      </div>
    </OverlayScrollbarsComponent>
  );
}

export default FindMyCar;
